import { defineMessages } from '@/i18n'

/** The project workflow: roles, gates, the team and each person's open work (docs/project-workflow-design.md). */
export const workflowMessages = defineMessages({
  en: {
    role_ceo: 'CEO', role_tender: 'Estimator', role_pm: 'Project Manager', role_prep: 'Permits & Planning', role_disp: 'Dispatcher',
    role_site: 'Site Manager', role_lead: 'Crew Leader', role_tech: 'Fiber Technician', role_store: 'Warehouse & Fleet',
    role_hse: 'Safety Officer', role_doc: 'Documentation', role_bill: 'Billing', role_acc: 'Accounting & Controlling', role_hr: 'HR & Compliance',
    role_admin: 'Admin',
    gate_negotiation: 'Offer sent', gate_contracted: 'Contract signed', gate_ready: 'Ready to start', gate_completed: 'Construction done',
    gate_accepted: 'Client acceptance', gate_closed: 'Paid and closed',
    item_negotiation_prices: 'Client price list entered in Billing',
    item_negotiation_estimate: 'Cost estimate with expected margin',
    item_negotiation_bid: 'Decision to bid recorded',
    item_contracted_contract_doc: 'Signed contract uploaded in AU',
    item_contracted_billing_terms: 'Contract prices and billing terms entered in Billing',
    item_contracted_plan_dates: 'Planned start and end dates',
    item_ready_permits: 'Permits and traffic orders for the first segments',
    item_ready_utilities: 'Utility records received for the first segments',
    item_ready_network_plan: 'Network plan: cable plan declared',
    item_ready_crews_cleared: 'Crews cleared by HR and safety (A1, work permits, briefing)',
    item_ready_cable_on_hand: 'Cable taken to the site store or issued to a crew',
    item_completed_built: 'All planned segments built and approved',
    item_completed_measured: 'All sections measured and passed',
    item_completed_restoration: 'Surface restoration done or scheduled',
    item_completed_damages: 'Damage on site repaired or closed',
    item_accepted_as_built: 'As-built documentation accepted by the client',
    item_accepted_acceptance: 'Acceptance protocol signed',
    item_accepted_defects: 'Defect list closed',
    item_closed_final_invoice: 'Final invoice issued (AU)',
    item_closed_final_paid: 'Final invoice paid (receivables)',
    item_closed_claims_settled: 'All hindrance notices and change orders decided and paid',
    item_closed_lessons: 'Lessons learned noted for the next offer',
    panelTitle: 'Workflow',
    nextGate: 'Next gate: {gate}',
    signedBy: 'Signed by the {role}',
    openOf: '{open} of {total} open',
    allDone: 'All items done',
    fromData: 'checked from data',
    owner: 'Owner',
    confirm: 'Confirm',
    undo: 'Undo',
    confirmTitle: 'Confirm: {item}',
    noteOptional: 'Note (optional)',
    notePlaceholder: 'e.g. permit number, valid until',
    sign: 'Sign',
    signTitle: 'Sign: {gate}',
    signHint: 'Records the project as "{state}" from this date.',
    date: 'Date',
    note: 'Note',
    overrideReason: 'Why go ahead with open items?',
    overrideHint: '{n} items are still open. The reason and the open items are kept with the state.',
    cantSign: 'Only the {role} signs this gate.',
    noGate: 'No further gate.',
    recordOther: 'Other state',
    recordTitle: 'Record a state',
    state: 'State',
    endNote: 'Why does the project end here?',
    team: 'Team',
    add: 'Add',
    remove: 'Remove {name}',
    nobody: 'No one',
    pickPerson: 'Choose a person',
    noPeople: 'No one holds this role yet. An admin assigns roles in Users.',
    auSiteManagers: 'Bauleiters on crew-days (AU)',
    crewDays: '{n} crew-days, last {date}',
    history: 'Signed states',
    signedAs: '{by} as {role}',
    wentAhead: 'went ahead: {reason}',
    myRoles: 'Your roles here: {roles}',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    couldNotSave: 'Could not save.',
    myWork: 'My work',
    myWorkSubtitle: 'Projects waiting for you: gate items you own, and gates you sign.',
    colProject: 'Project',
    colState: 'State',
    colGate: 'Next gate',
    colMine: 'Waiting on you',
    readyToSign: 'Ready to sign',
    youSign: 'You sign',
    needsPm: 'No Project Manager',
    nothing: 'Nothing is waiting for you.',
    noRoles: 'You hold no role yet. An admin assigns roles in Users.',
    loading: 'Loading your work',
    open: 'Open',
  },
  de: {
    role_ceo: 'Geschäftsführung', role_tender: 'Kalkulation', role_pm: 'Projektleitung', role_prep: 'Arbeitsvorbereitung', role_disp: 'Disposition',
    role_site: 'Bauleitung', role_lead: 'Kolonnenführer', role_tech: 'Spleiß- & Messtechnik', role_store: 'Lager & Fuhrpark',
    role_hse: 'Sicherheitsfachkraft', role_doc: 'Dokumentation', role_bill: 'Abrechnung', role_acc: 'Buchhaltung & Controlling', role_hr: 'Personal',
    role_admin: 'Admin',
    gate_negotiation: 'Angebot abgegeben', gate_contracted: 'Vertrag unterschrieben', gate_ready: 'Startklar', gate_completed: 'Bau fertig',
    gate_accepted: 'Abnahme', gate_closed: 'Bezahlt und abgeschlossen',
    item_negotiation_prices: 'Preisliste des Kunden in der Abrechnung erfasst',
    item_negotiation_estimate: 'Kalkulation mit erwarteter Marge',
    item_negotiation_bid: 'Entscheidung zur Abgabe festgehalten',
    item_contracted_contract_doc: 'Unterschriebener Vertrag in AU hochgeladen',
    item_contracted_billing_terms: 'Vertragspreise und Abrechnungsbedingungen in der Abrechnung',
    item_contracted_plan_dates: 'Geplanter Start und geplantes Ende',
    item_ready_permits: 'Aufgrabegenehmigungen und VRAO für die ersten Abschnitte',
    item_ready_utilities: 'Spartenauskunft für die ersten Abschnitte liegt vor',
    item_ready_network_plan: 'Netzplan: Kabelplan erfasst',
    item_ready_crews_cleared: 'Kolonnen von Personal und Arbeitssicherheit freigegeben (A1, Arbeitserlaubnis, Unterweisung)',
    item_ready_cable_on_hand: 'Kabel im Baustellenlager oder an eine Kolonne ausgegeben',
    item_completed_built: 'Alle geplanten Abschnitte gebaut und abgenommen',
    item_completed_measured: 'Alle Abschnitte gemessen und bestanden',
    item_completed_restoration: 'Oberflächen wiederhergestellt oder eingeplant',
    item_completed_damages: 'Schäden repariert oder abgeschlossen',
    item_accepted_as_built: 'Bestandsdokumentation vom Kunden angenommen',
    item_accepted_acceptance: 'Abnahmeprotokoll unterschrieben',
    item_accepted_defects: 'Mängelliste geschlossen',
    item_closed_final_invoice: 'Schlussrechnung gestellt (AU)',
    item_closed_final_paid: 'Schlussrechnung bezahlt (Debitoren)',
    item_closed_claims_settled: 'Behinderungen und Nachträge entschieden und bezahlt',
    item_closed_lessons: 'Erkenntnisse für das nächste Angebot festgehalten',
    panelTitle: 'Ablauf',
    nextGate: 'Nächstes Tor: {gate}',
    signedBy: 'Unterschreibt: {role}',
    openOf: '{open} von {total} offen',
    allDone: 'Alle Punkte erledigt',
    fromData: 'aus den Daten geprüft',
    owner: 'Zuständig',
    confirm: 'Bestätigen',
    undo: 'Zurücknehmen',
    confirmTitle: 'Bestätigen: {item}',
    noteOptional: 'Notiz (optional)',
    notePlaceholder: 'z. B. Genehmigungsnummer, gültig bis',
    sign: 'Unterschreiben',
    signTitle: 'Unterschreiben: {gate}',
    signHint: 'Setzt das Projekt ab diesem Datum auf „{state}“.',
    date: 'Datum',
    note: 'Notiz',
    overrideReason: 'Warum trotz offener Punkte weiter?',
    overrideHint: '{n} Punkte sind noch offen. Begründung und offene Punkte werden mit dem Status gespeichert.',
    cantSign: 'Dieses Tor unterschreibt nur: {role}.',
    noGate: 'Kein weiteres Tor.',
    recordOther: 'Anderer Status',
    recordTitle: 'Status erfassen',
    state: 'Status',
    endNote: 'Warum endet das Projekt hier?',
    team: 'Team',
    add: 'Hinzufügen',
    remove: '{name} entfernen',
    nobody: 'Niemand',
    pickPerson: 'Person wählen',
    noPeople: 'Diese Rolle hat noch niemand. Rollen vergibt ein Admin unter Benutzer.',
    auSiteManagers: 'Bauleiter laut Kolonnentagen (AU)',
    crewDays: '{n} Kolonnentage, zuletzt {date}',
    history: 'Unterschriebene Status',
    signedAs: '{by} als {role}',
    wentAhead: 'trotzdem weiter: {reason}',
    myRoles: 'Ihre Rollen hier: {roles}',
    save: 'Speichern',
    saving: 'Speichert…',
    cancel: 'Abbrechen',
    couldNotSave: 'Konnte nicht speichern.',
    myWork: 'Meine Aufgaben',
    myWorkSubtitle: 'Projekte, die auf Sie warten: Punkte, für die Sie zuständig sind, und Tore, die Sie unterschreiben.',
    colProject: 'Projekt',
    colState: 'Status',
    colGate: 'Nächstes Tor',
    colMine: 'Wartet auf Sie',
    readyToSign: 'Bereit zum Unterschreiben',
    youSign: 'Sie unterschreiben',
    needsPm: 'Keine Projektleitung',
    nothing: 'Nichts wartet auf Sie.',
    noRoles: 'Sie haben noch keine Rolle. Rollen vergibt ein Admin unter Benutzer.',
    loading: 'Aufgaben werden geladen',
    open: 'Öffnen',
  },
  cnr: {
    role_ceo: 'Direktor (CEO)', role_tender: 'Kalkulant', role_pm: 'Projekt menadžer', role_prep: 'Dozvole i planiranje', role_disp: 'Dispečer',
    role_site: 'Šef gradilišta (Bauleiter)', role_lead: 'Vođa kolone', role_tech: 'Tehničar za vlakna', role_store: 'Magacin i vozni park',
    role_hse: 'Referent zaštite na radu', role_doc: 'Dokumentacija', role_bill: 'Naplata', role_acc: 'Računovodstvo i kontroling', role_hr: 'Kadrovska služba',
    role_admin: 'Admin',
    gate_negotiation: 'Ponuda poslata', gate_contracted: 'Ugovor potpisan', gate_ready: 'Spremno za početak', gate_completed: 'Gradnja završena',
    gate_accepted: 'Prijem od klijenta', gate_closed: 'Plaćeno i zatvoreno',
    item_negotiation_prices: 'Cjenovnik klijenta unesen u Naplatu',
    item_negotiation_estimate: 'Kalkulacija s očekivanom maržom',
    item_negotiation_bid: 'Odluka o ponudi zabilježena',
    item_contracted_contract_doc: 'Potpisan ugovor učitan u AU',
    item_contracted_billing_terms: 'Ugovorene cijene i uslovi naplate uneseni u Naplatu',
    item_contracted_plan_dates: 'Planirani početak i kraj',
    item_ready_permits: 'Dozvole za kopanje i saobraćaj za prve dionice',
    item_ready_utilities: 'Podaci o instalacijama stigli za prve dionice',
    item_ready_network_plan: 'Plan mreže: plan kablova unesen',
    item_ready_crews_cleared: 'Kolone odobrile kadrovska služba i zaštita na radu (A1, radne dozvole, obuka)',
    item_ready_cable_on_hand: 'Kabl u skladištu gradilišta ili izdat koloni',
    item_completed_built: 'Sve planirane dionice izgrađene i odobrene',
    item_completed_measured: 'Sve dionice izmjerene i prošle',
    item_completed_restoration: 'Površine vraćene ili zakazane',
    item_completed_damages: 'Štete popravljene ili zatvorene',
    item_accepted_as_built: 'Klijent prihvatio dokumentaciju izvedenog stanja',
    item_accepted_acceptance: 'Zapisnik o prijemu potpisan',
    item_accepted_defects: 'Lista nedostataka zatvorena',
    item_closed_final_invoice: 'Konačna faktura izdata (AU)',
    item_closed_final_paid: 'Konačna faktura plaćena (potraživanja)',
    item_closed_claims_settled: 'Smetnje i Nachtrazi riješeni i plaćeni',
    item_closed_lessons: 'Pouke zabilježene za sljedeću ponudu',
    panelTitle: 'Tok projekta',
    nextGate: 'Sljedeća kapija: {gate}',
    signedBy: 'Potpisuje: {role}',
    openOf: '{open} od {total} otvoreno',
    allDone: 'Sve stavke završene',
    fromData: 'provjereno iz podataka',
    owner: 'Zadužen',
    confirm: 'Potvrdi',
    undo: 'Poništi',
    confirmTitle: 'Potvrdi: {item}',
    noteOptional: 'Napomena (neobavezno)',
    notePlaceholder: 'npr. broj dozvole, važi do',
    sign: 'Potpiši',
    signTitle: 'Potpiši: {gate}',
    signHint: 'Upisuje projekat kao „{state}” od ovog datuma.',
    date: 'Datum',
    note: 'Napomena',
    overrideReason: 'Zašto nastaviti s otvorenim stavkama?',
    overrideHint: '{n} stavki je još otvoreno. Razlog i otvorene stavke čuvaju se uz stanje.',
    cantSign: 'Ovu kapiju potpisuje samo: {role}.',
    noGate: 'Nema sljedeće kapije.',
    recordOther: 'Drugo stanje',
    recordTitle: 'Upiši stanje',
    state: 'Stanje',
    endNote: 'Zašto se projekat ovdje završava?',
    team: 'Tim',
    add: 'Dodaj',
    remove: 'Ukloni: {name}',
    nobody: 'Niko',
    pickPerson: 'Izaberi osobu',
    noPeople: 'Ovu ulogu još niko nema. Uloge dodjeljuje admin u Korisnicima.',
    auSiteManagers: 'Bauleiteri po danima kolone (AU)',
    crewDays: '{n} dana kolone, zadnji {date}',
    history: 'Potpisana stanja',
    signedAs: '{by} kao {role}',
    wentAhead: 'nastavljeno: {reason}',
    myRoles: 'Vaše uloge ovdje: {roles}',
    save: 'Sačuvaj',
    saving: 'Čuva se…',
    cancel: 'Otkaži',
    couldNotSave: 'Nije sačuvano.',
    myWork: 'Moji zadaci',
    myWorkSubtitle: 'Projekti koji čekaju na vas: stavke za koje ste zaduženi i kapije koje potpisujete.',
    colProject: 'Projekat',
    colState: 'Stanje',
    colGate: 'Sljedeća kapija',
    colMine: 'Čeka na vas',
    readyToSign: 'Spremno za potpis',
    youSign: 'Vi potpisujete',
    needsPm: 'Nema projekt menadžera',
    nothing: 'Ništa ne čeka na vas.',
    noRoles: 'Još nemate ulogu. Uloge dodjeljuje admin u Korisnicima.',
    loading: 'Učitavanje zadataka',
    open: 'Otvori',
  },
  bs: {
    role_ceo: 'Direktor (CEO)', role_tender: 'Kalkulant', role_pm: 'Projekt menadžer', role_prep: 'Dozvole i planiranje', role_disp: 'Dispečer',
    role_site: 'Šef gradilišta (Bauleiter)', role_lead: 'Vođa kolone', role_tech: 'Tehničar za vlakna', role_store: 'Magacin i vozni park',
    role_hse: 'Referent zaštite na radu', role_doc: 'Dokumentacija', role_bill: 'Naplata', role_acc: 'Računovodstvo i kontroling', role_hr: 'Kadrovska služba',
    role_admin: 'Admin',
    gate_negotiation: 'Ponuda poslana', gate_contracted: 'Ugovor potpisan', gate_ready: 'Spremno za početak', gate_completed: 'Gradnja završena',
    gate_accepted: 'Prijem od klijenta', gate_closed: 'Plaćeno i zatvoreno',
    item_negotiation_prices: 'Cjenovnik klijenta unesen u Naplatu',
    item_negotiation_estimate: 'Kalkulacija s očekivanom maržom',
    item_negotiation_bid: 'Odluka o ponudi zabilježena',
    item_contracted_contract_doc: 'Potpisan ugovor učitan u AU',
    item_contracted_billing_terms: 'Ugovorene cijene i uslovi naplate uneseni u Naplatu',
    item_contracted_plan_dates: 'Planirani početak i kraj',
    item_ready_permits: 'Dozvole za kopanje i saobraćaj za prve dionice',
    item_ready_utilities: 'Podaci o instalacijama stigli za prve dionice',
    item_ready_network_plan: 'Plan mreže: plan kablova unesen',
    item_ready_crews_cleared: 'Kolone odobrile kadrovska služba i zaštita na radu (A1, radne dozvole, obuka)',
    item_ready_cable_on_hand: 'Kabl u skladištu gradilišta ili izdat koloni',
    item_completed_built: 'Sve planirane dionice izgrađene i odobrene',
    item_completed_measured: 'Sve dionice izmjerene i prošle',
    item_completed_restoration: 'Površine vraćene ili zakazane',
    item_completed_damages: 'Štete popravljene ili zatvorene',
    item_accepted_as_built: 'Klijent prihvatio dokumentaciju izvedenog stanja',
    item_accepted_acceptance: 'Zapisnik o prijemu potpisan',
    item_accepted_defects: 'Lista nedostataka zatvorena',
    item_closed_final_invoice: 'Konačna faktura izdata (AU)',
    item_closed_final_paid: 'Konačna faktura plaćena (potraživanja)',
    item_closed_claims_settled: 'Smetnje i Nachtrazi riješeni i plaćeni',
    item_closed_lessons: 'Pouke zabilježene za sljedeću ponudu',
    panelTitle: 'Tok projekta',
    nextGate: 'Sljedeća kapija: {gate}',
    signedBy: 'Potpisuje: {role}',
    openOf: '{open} od {total} otvoreno',
    allDone: 'Sve stavke završene',
    fromData: 'provjereno iz podataka',
    owner: 'Zadužen',
    confirm: 'Potvrdi',
    undo: 'Poništi',
    confirmTitle: 'Potvrdi: {item}',
    noteOptional: 'Napomena (neobavezno)',
    notePlaceholder: 'npr. broj dozvole, važi do',
    sign: 'Potpiši',
    signTitle: 'Potpiši: {gate}',
    signHint: 'Upisuje projekat kao „{state}” od ovog datuma.',
    date: 'Datum',
    note: 'Napomena',
    overrideReason: 'Zašto nastaviti s otvorenim stavkama?',
    overrideHint: '{n} stavki je još otvoreno. Razlog i otvorene stavke čuvaju se uz stanje.',
    cantSign: 'Ovu kapiju potpisuje samo: {role}.',
    noGate: 'Nema sljedeće kapije.',
    recordOther: 'Drugo stanje',
    recordTitle: 'Upiši stanje',
    state: 'Stanje',
    endNote: 'Zašto se projekat ovdje završava?',
    team: 'Tim',
    add: 'Dodaj',
    remove: 'Ukloni: {name}',
    nobody: 'Niko',
    pickPerson: 'Izaberi osobu',
    noPeople: 'Ovu ulogu još niko nema. Uloge dodjeljuje admin u Korisnicima.',
    auSiteManagers: 'Bauleiteri po danima kolone (AU)',
    crewDays: '{n} dana kolone, zadnji {date}',
    history: 'Potpisana stanja',
    signedAs: '{by} kao {role}',
    wentAhead: 'nastavljeno: {reason}',
    myRoles: 'Vaše uloge ovdje: {roles}',
    save: 'Sačuvaj',
    saving: 'Čuva se…',
    cancel: 'Otkaži',
    couldNotSave: 'Nije sačuvano.',
    myWork: 'Moji zadaci',
    myWorkSubtitle: 'Projekti koji čekaju na vas: stavke za koje ste zaduženi i kapije koje potpisujete.',
    colProject: 'Projekat',
    colState: 'Stanje',
    colGate: 'Sljedeća kapija',
    colMine: 'Čeka na vas',
    readyToSign: 'Spremno za potpis',
    youSign: 'Vi potpisujete',
    needsPm: 'Nema projekt menadžera',
    nothing: 'Ništa ne čeka na vas.',
    noRoles: 'Još nemate ulogu. Uloge dodjeljuje admin u Korisnicima.',
    loading: 'Učitavanje zadataka',
    open: 'Otvori',
  },
  sr: {
    role_ceo: 'Direktor (CEO)', role_tender: 'Kalkulant', role_pm: 'Projekt menadžer', role_prep: 'Dozvole i planiranje', role_disp: 'Dispečer',
    role_site: 'Šef gradilišta (Bauleiter)', role_lead: 'Vođa kolone', role_tech: 'Tehničar za vlakna', role_store: 'Magacin i vozni park',
    role_hse: 'Referent bezbednosti na radu', role_doc: 'Dokumentacija', role_bill: 'Naplata', role_acc: 'Računovodstvo i kontroling', role_hr: 'Kadrovska služba',
    role_admin: 'Admin',
    gate_negotiation: 'Ponuda poslata', gate_contracted: 'Ugovor potpisan', gate_ready: 'Spremno za početak', gate_completed: 'Gradnja završena',
    gate_accepted: 'Prijem od klijenta', gate_closed: 'Plaćeno i zatvoreno',
    item_negotiation_prices: 'Cenovnik klijenta unet u Naplatu',
    item_negotiation_estimate: 'Kalkulacija sa očekivanom maržom',
    item_negotiation_bid: 'Odluka o ponudi zabeležena',
    item_contracted_contract_doc: 'Potpisan ugovor učitan u AU',
    item_contracted_billing_terms: 'Ugovorene cene i uslovi naplate uneti u Naplatu',
    item_contracted_plan_dates: 'Planirani početak i kraj',
    item_ready_permits: 'Dozvole za kopanje i saobraćaj za prve deonice',
    item_ready_utilities: 'Podaci o instalacijama stigli za prve deonice',
    item_ready_network_plan: 'Plan mreže: plan kablova unet',
    item_ready_crews_cleared: 'Kolone odobrile kadrovska služba i bezbednost na radu (A1, radne dozvole, obuka)',
    item_ready_cable_on_hand: 'Kabl u skladištu gradilišta ili izdat koloni',
    item_completed_built: 'Sve planirane deonice izgrađene i odobrene',
    item_completed_measured: 'Sve deonice izmerene i prošle',
    item_completed_restoration: 'Površine vraćene ili zakazane',
    item_completed_damages: 'Štete popravljene ili zatvorene',
    item_accepted_as_built: 'Klijent prihvatio dokumentaciju izvedenog stanja',
    item_accepted_acceptance: 'Zapisnik o prijemu potpisan',
    item_accepted_defects: 'Lista nedostataka zatvorena',
    item_closed_final_invoice: 'Konačna faktura izdata (AU)',
    item_closed_final_paid: 'Konačna faktura plaćena (potraživanja)',
    item_closed_claims_settled: 'Smetnje i Nachtrazi rešeni i plaćeni',
    item_closed_lessons: 'Pouke zabeležene za sledeću ponudu',
    panelTitle: 'Tok projekta',
    nextGate: 'Sledeća kapija: {gate}',
    signedBy: 'Potpisuje: {role}',
    openOf: '{open} od {total} otvoreno',
    allDone: 'Sve stavke završene',
    fromData: 'provereno iz podataka',
    owner: 'Zadužen',
    confirm: 'Potvrdi',
    undo: 'Poništi',
    confirmTitle: 'Potvrdi: {item}',
    noteOptional: 'Napomena (neobavezno)',
    notePlaceholder: 'npr. broj dozvole, važi do',
    sign: 'Potpiši',
    signTitle: 'Potpiši: {gate}',
    signHint: 'Upisuje projekat kao „{state}” od ovog datuma.',
    date: 'Datum',
    note: 'Napomena',
    overrideReason: 'Zašto nastaviti sa otvorenim stavkama?',
    overrideHint: '{n} stavki je još otvoreno. Razlog i otvorene stavke čuvaju se uz stanje.',
    cantSign: 'Ovu kapiju potpisuje samo: {role}.',
    noGate: 'Nema sledeće kapije.',
    recordOther: 'Drugo stanje',
    recordTitle: 'Upiši stanje',
    state: 'Stanje',
    endNote: 'Zašto se projekat ovde završava?',
    team: 'Tim',
    add: 'Dodaj',
    remove: 'Ukloni: {name}',
    nobody: 'Niko',
    pickPerson: 'Izaberi osobu',
    noPeople: 'Ovu ulogu još niko nema. Uloge dodeljuje admin u Korisnicima.',
    auSiteManagers: 'Bauleiteri po danima kolone (AU)',
    crewDays: '{n} dana kolone, poslednji {date}',
    history: 'Potpisana stanja',
    signedAs: '{by} kao {role}',
    wentAhead: 'nastavljeno: {reason}',
    myRoles: 'Vaše uloge ovde: {roles}',
    save: 'Sačuvaj',
    saving: 'Čuva se…',
    cancel: 'Otkaži',
    couldNotSave: 'Nije sačuvano.',
    myWork: 'Moji zadaci',
    myWorkSubtitle: 'Projekti koji čekaju na vas: stavke za koje ste zaduženi i kapije koje potpisujete.',
    colProject: 'Projekat',
    colState: 'Stanje',
    colGate: 'Sledeća kapija',
    colMine: 'Čeka na vas',
    readyToSign: 'Spremno za potpis',
    youSign: 'Vi potpisujete',
    needsPm: 'Nema projekt menadžera',
    nothing: 'Ništa ne čeka na vas.',
    noRoles: 'Još nemate ulogu. Uloge dodeljuje admin u Korisnicima.',
    loading: 'Učitavanje zadataka',
    open: 'Otvori',
  },
})
